/**
 * Developers — Portal público de documentação da API AgentUS v1.
 *
 * v0.99.2 · Fev/2026 · Rota pública `/desenvolvedores` (sem auth).
 * Foco: sales enablement técnico. Deve fazer o dev do cliente pensar
 * "essa API é séria" nos primeiros 15 segundos e sair com todo o
 * material necessário para implementar em ~1 dia.
 *
 * Design: mesma linguagem visual do login (dark cinematic, glass,
 * tipografia sóbria) — vira propaganda visual do próprio produto.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Copy, Check, ArrowRight, Terminal, Key, Broadcast, Lightning,
  CaretDown, ShieldCheck, Code, Package, User as UserIcon,
  Calendar, ChatCircle, BookOpen, Warning, Question, PlayCircle, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

const BASE = "https://app.agentus.com.br/api/v1";

// ─────────────────────────────────────────────────────────
//  Reusable UI pieces
// ─────────────────────────────────────────────────────────

function CodeBlock({ code, lang = "bash" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setDone(true);
      toast.success("Copiado");
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Falha ao copiar");
    }
  };
  return (
    <div className="relative group">
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-3 py-1.5 border-b border-white/[0.05] bg-white/[0.02]">
        <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-white/40">{lang}</span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-[10px] text-white/40 hover:text-white/90 transition-colors"
        >
          {done ? <Check size={11} weight="bold" /> : <Copy size={11} />}
          {done ? "Copiado" : "Copiar"}
        </button>
      </div>
      <pre className="pt-10 pb-4 px-4 text-[12.5px] leading-[1.55] text-emerald-200/90 font-mono overflow-x-auto rounded-xl border border-white/[0.06] bg-[#0A0D14] whitespace-pre">
        {code}
      </pre>
    </div>
  );
}

function Section({ id, eyebrow, title, subtitle, children }) {
  return (
    <section id={id} className="scroll-mt-24 py-16 md:py-20">
      {eyebrow && (
        <div className="text-[9.5px] uppercase tracking-[0.3em] text-emerald-300/70 font-medium mb-3">
          {eyebrow}
        </div>
      )}
      <h2 className="font-heading text-[26px] md:text-[34px] font-semibold tracking-[-0.028em] text-white leading-[1.15]">
        {title}
      </h2>
      {subtitle && (
        <p className="text-[14px] md:text-[15px] text-white/50 mt-3 max-w-2xl tracking-tight leading-relaxed">
          {subtitle}
        </p>
      )}
      <div className="mt-8">{children}</div>
    </section>
  );
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-xl border border-white/[0.08] p-5 md:p-6 ${className}`}
      style={{
        background: "rgba(255,255,255,0.025)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      {children}
    </div>
  );
}

function Pill({ children, tone = "default" }) {
  const tones = {
    default: "border-white/10 bg-white/5 text-white/70",
    green: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-300",
    blue: "border-sky-400/30 bg-sky-400/[0.06] text-sky-300",
    amber: "border-amber-400/30 bg-amber-400/[0.06] text-amber-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-mono px-2 py-0.5 rounded border ${tones[tone]}`}>
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────
//  Sections
// ─────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative pt-24 pb-16 md:pt-32 md:pb-24">
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(760px 480px at 50% 30%, rgba(52,168,83,0.12), transparent 65%), radial-gradient(900px 500px at 20% 90%, rgba(96,140,255,0.06), transparent 70%)",
        }}
      />
      <div className="relative z-10 max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/[0.09] bg-white/[0.03] px-3 py-1.5 mb-6">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.24em] text-white/60 font-medium">
            API v1 · Estável
          </span>
        </div>
        <h1 className="font-heading text-[36px] sm:text-[52px] md:text-[68px] font-semibold tracking-[-0.035em] text-white leading-[1.02]">
          AgentUS API. <br />
          <span className="text-white/45">Conecte tudo.</span>
        </h1>
        <p className="text-[15px] md:text-[17px] text-white/55 mt-5 max-w-2xl mx-auto tracking-tight leading-relaxed">
          Sincronize catálogo, receba agendamentos em tempo real e envie mensagens
          diretamente do seu ERP, CRM ou sistema próprio.
          <br />
          <span className="text-white/35">REST · JSON · Webhooks HMAC · RFC 7807 · Idempotency-Key.</span>
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <a
            href="#quickstart"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-[#0B0F17] font-semibold text-[13px] hover:bg-slate-100 transition-colors"
            data-testid="hero-cta-quickstart"
          >
            Começar em 5 minutos
            <ArrowRight size={13} weight="bold" />
          </a>
          <a
            href="#playground"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-emerald-400/40 text-emerald-200 font-medium text-[13px] hover:bg-emerald-400/[0.06] transition-colors"
            data-testid="hero-cta-playground"
          >
            <PlayCircle size={14} weight="fill" />
            Testar API ao vivo
          </a>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
          <StatCard icon={Lightning} label="Latência típica" value="< 200ms" />
          <StatCard icon={Package} label="Bulk upsert" value="1.000 SKUs/req" />
          <StatCard icon={Broadcast} label="Retry webhook" value="6 tentativas" />
          <StatCard icon={ShieldCheck} label="Auditável" value="100% dos calls" />
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-left">
      <Icon size={15} className="text-emerald-400/80 mb-1.5" />
      <div className="text-[11px] text-white/40 tracking-tight">{label}</div>
      <div className="text-[15px] text-white font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function QuickStart() {
  const steps = [
    {
      n: "01",
      icon: Key,
      title: "Gere sua API key",
      desc: "No painel AgentUS: Configuração › Desenvolvedor › Nova chave. Escolha ambiente Live ou Test e os escopos necessários. A chave aparece uma única vez — copie na hora.",
      code: `agentus_live_sk_5N9TxJgVh82pQmRz3kY4W1cL7DfBhXaU`,
      lang: "chave gerada",
    },
    {
      n: "02",
      icon: Terminal,
      title: "Faça sua primeira chamada",
      desc: "Autentique com o header Authorization Bearer. Todas as chamadas usam JSON, retornam JSON, e falham no padrão RFC 7807.",
      code: `curl ${BASE}/customers \\
  -H "Authorization: Bearer agentus_live_sk_..."`,
      lang: "cURL",
    },
    {
      n: "03",
      icon: Broadcast,
      title: "Escute os eventos",
      desc: "Cadastre uma URL HTTPS no painel para receber notificações quando algo acontecer no AgentUS. Assinatura HMAC-SHA256 no header X-Agentus-Signature.",
      code: `POST /webhook/agentus  →  {
  "type": "appointment.created",
  "data": { ... }
}`,
      lang: "payload recebido",
    },
  ];

  return (
    <Section
      id="quickstart"
      eyebrow="Começar rápido"
      title="Do zero à primeira chamada em 5 minutos."
      subtitle="Três passos simples. Zero SDK. Zero infra. Funciona com qualquer linguagem que fale HTTP."
    >
      <div className="grid md:grid-cols-3 gap-4">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.n} className="flex flex-col">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[10.5px] font-mono text-white/30">{s.n}</span>
                <span className="w-8 h-8 rounded-lg border border-white/[0.08] bg-white/[0.03] flex items-center justify-center">
                  <Icon size={15} className="text-emerald-300/90" />
                </span>
              </div>
              <h3 className="text-[15px] font-semibold text-white tracking-tight mb-1.5">{s.title}</h3>
              <p className="text-[12.5px] text-white/45 leading-relaxed mb-4">{s.desc}</p>
              <div className="mt-auto">
                <CodeBlock code={s.code} lang={s.lang} />
              </div>
            </Card>
          );
        })}
      </div>
    </Section>
  );
}

function Endpoints() {
  const groups = [
    {
      id: "products",
      icon: Package,
      title: "Produtos",
      subtitle: "Catálogo com SKU, preço, categoria, estoque. Bulk-upsert pra sincronizar do ERP.",
      routes: [
        { method: "GET", path: "/products", desc: "Lista paginada" },
        { method: "GET", path: "/products/{id}", desc: "Detalhe" },
        { method: "POST", path: "/products", desc: "Criar" },
        { method: "PUT", path: "/products/{id}", desc: "Atualizar" },
        { method: "POST", path: "/products/bulk-upsert", desc: "Sincronizar catálogo inteiro (crown jewel)", featured: true },
      ],
      example: `curl -X POST ${BASE}/products/bulk-upsert \\
  -H "Authorization: Bearer agentus_live_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "items": [
      {
        "sku": "RC15KG",
        "name": "Ração Royal Canin Adult 15kg",
        "price_cents": 28900,
        "category": "racao",
        "in_stock": true
      },
      {
        "sku": "PREM15",
        "name": "Premier Formula 15kg",
        "price_cents": 24500,
        "category": "racao",
        "in_stock": true
      }
    ]
  }'`,
    },
    {
      id: "customers",
      icon: UserIcon,
      title: "Clientes",
      subtitle: "Cadastro deduplicado por telefone. IDs estáveis para referência cruzada com seu ERP.",
      routes: [
        { method: "GET", path: "/customers", desc: "Lista com filtro por nome/telefone/email" },
        { method: "GET", path: "/customers/{id}", desc: "Detalhe" },
        { method: "POST", path: "/customers", desc: "Criar (dedup por telefone)" },
        { method: "PUT", path: "/customers/{id}", desc: "Atualizar" },
      ],
      example: `curl -X POST ${BASE}/customers \\
  -H "Authorization: Bearer agentus_live_sk_..." \\
  -H "Idempotency-Key: 550e8400-e29b-41d4-a716-446655440000" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "João Silva",
    "phone": "+5511977776666",
    "email": "joao@example.com"
  }'`,
    },
    {
      id: "appointments",
      icon: Calendar,
      title: "Agendamentos",
      subtitle: "Marca e cancela agendamentos. Emite webhook toda vez.",
      routes: [
        { method: "GET", path: "/appointments", desc: "Lista com filtro por status" },
        { method: "POST", path: "/appointments", desc: "Criar (customer_id deve pertencer ao tenant)" },
        { method: "POST", path: "/appointments/{id}/cancel", desc: "Cancelar" },
      ],
      example: `curl -X POST ${BASE}/appointments \\
  -H "Authorization: Bearer agentus_live_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_id": "cust_789",
    "service_code": "banho_tosa",
    "scheduled_at": "2026-08-20T10:00:00Z",
    "duration_min": 60
  }'`,
    },
    {
      id: "messages",
      icon: ChatCircle,
      title: "Conversas & Mensagens",
      subtitle: "Leia histórico do WhatsApp e envie mensagens outbound programaticamente.",
      routes: [
        { method: "GET", path: "/conversations", desc: "Lista de conversas" },
        { method: "GET", path: "/conversations/{id}/messages", desc: "Mensagens ASC" },
        { method: "POST", path: "/conversations/{id}/messages", desc: "Enviar mensagem outbound" },
      ],
      example: `curl -X POST ${BASE}/conversations/conv_abc/messages \\
  -H "Authorization: Bearer agentus_live_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "body": "Sua ração já chegou! Passa aqui pra retirar."
  }'`,
    },
    {
      id: "brain",
      icon: BookOpen,
      title: "Cérebro (Knowledge Base)",
      subtitle: "Empurre documentos institucionais que a IA vai usar como fonte de resposta.",
      routes: [
        { method: "POST", path: "/brain/documents", desc: "Cadastra documento no Cérebro global" },
      ],
      example: `curl -X POST ${BASE}/brain/documents \\
  -H "Authorization: Bearer agentus_live_sk_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Política de Troca",
    "content": "Aceitamos trocas em até 7 dias com nota fiscal...",
    "category": "politicas",
    "tags": ["troca", "devolucao"]
  }'`,
    },
  ];

  const [active, setActive] = useState("products");
  const current = groups.find((g) => g.id === active);

  return (
    <Section
      id="endpoints"
      eyebrow="Referência"
      title="Endpoints v1."
      subtitle="Todos os recursos essenciais para integrar seu sistema com o AgentUS."
    >
      <div className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-6">
        {/* Nav lateral */}
        <nav className="flex md:flex-col gap-2 overflow-x-auto pb-2 md:pb-0">
          {groups.map((g) => {
            const Icon = g.icon;
            const isActive = active === g.id;
            return (
              <button
                key={g.id}
                onClick={() => setActive(g.id)}
                data-testid={`endpoint-nav-${g.id}`}
                className={`shrink-0 md:shrink text-left px-3 py-2.5 rounded-lg transition-all inline-flex items-center gap-2.5 ${
                  isActive
                    ? "bg-white/[0.06] text-white border border-white/[0.08]"
                    : "text-white/45 hover:text-white/80 hover:bg-white/[0.02] border border-transparent"
                }`}
              >
                <Icon size={14} className={isActive ? "text-emerald-300" : ""} />
                <span className="text-[13px] font-medium tracking-tight whitespace-nowrap md:whitespace-normal">
                  {g.title}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Detalhe */}
        <Card>
          <div className="flex items-start gap-3 mb-4 pb-4 border-b border-white/[0.05]">
            <current.icon size={22} className="text-emerald-300/90 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-[17px] font-semibold text-white tracking-tight">{current.title}</h3>
              <p className="text-[12.5px] text-white/45 mt-1 leading-relaxed">{current.subtitle}</p>
            </div>
          </div>

          <div className="space-y-1.5 mb-6">
            {current.routes.map((r) => (
              <div
                key={r.path + r.method}
                className={`flex items-center gap-3 py-2 px-2.5 rounded-lg ${
                  r.featured ? "bg-emerald-400/[0.04] border border-emerald-400/15" : ""
                }`}
              >
                <MethodBadge method={r.method} />
                <code className="text-[12.5px] font-mono text-white/90 flex-1">{r.path}</code>
                <span className="text-[11.5px] text-white/45 hidden sm:block text-right">
                  {r.desc}
                </span>
              </div>
            ))}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-medium mb-2">
              Exemplo
            </div>
            <CodeBlock code={current.example} lang="cURL" />
          </div>
        </Card>
      </div>
    </Section>
  );
}

function MethodBadge({ method }) {
  const map = {
    GET:    "bg-sky-400/[0.08] text-sky-300 border-sky-400/20",
    POST:   "bg-emerald-400/[0.08] text-emerald-300 border-emerald-400/20",
    PUT:    "bg-amber-400/[0.08] text-amber-300 border-amber-400/20",
    DELETE: "bg-red-400/[0.08] text-red-300 border-red-400/20",
  };
  return (
    <span className={`inline-flex items-center justify-center text-[9.5px] font-mono font-semibold uppercase w-[44px] py-0.5 rounded border tracking-wider ${map[method] || map.GET}`}>
      {method}
    </span>
  );
}

// ─────────────────────────────────────────────────────────
//  Playground — testador interativo da API
// ─────────────────────────────────────────────────────────

const PLAYGROUND_PRESETS = [
  {
    id: "list_products",
    label: "Listar produtos",
    method: "GET",
    path: "/products?limit=5",
    scope: "products:read",
    body: null,
  },
  {
    id: "list_customers",
    label: "Listar clientes",
    method: "GET",
    path: "/customers?limit=5",
    scope: "customers:read",
    body: null,
  },
  {
    id: "create_customer",
    label: "Criar cliente",
    method: "POST",
    path: "/customers",
    scope: "customers:write",
    body: {
      name: "Cliente Teste API",
      phone: "+5511977776666",
      email: "teste@example.com",
    },
  },
  {
    id: "bulk_upsert",
    label: "Bulk-upsert produtos",
    method: "POST",
    path: "/products/bulk-upsert",
    scope: "products:write",
    body: {
      items: [
        {
          sku: "TESTE-01",
          name: "Produto de Teste 1",
          price_cents: 9900,
          category: "teste",
          in_stock: true,
        },
        {
          sku: "TESTE-02",
          name: "Produto de Teste 2",
          price_cents: 15900,
          category: "teste",
          in_stock: true,
        },
      ],
    },
  },
];

const PLAYGROUND_KEY_STORAGE = "agentus_playground_key";

function Playground() {
  const [preset, setPreset] = useState(PLAYGROUND_PRESETS[0]);
  const [apiKey, setApiKey] = useState(() => {
    try { return localStorage.getItem(PLAYGROUND_KEY_STORAGE) || ""; }
    catch { return ""; }
  });
  const [bodyText, setBodyText] = useState(
    preset.body ? JSON.stringify(preset.body, null, 2) : ""
  );
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const selectPreset = (p) => {
    setPreset(p);
    setBodyText(p.body ? JSON.stringify(p.body, null, 2) : "");
    setResponse(null);
  };

  const saveKey = (v) => {
    setApiKey(v);
    try { localStorage.setItem(PLAYGROUND_KEY_STORAGE, v); } catch (_) { /* ignore */ }
  };

  const isTestKey = apiKey.startsWith("agentus_test_sk_");
  const isLiveKey = apiKey.startsWith("agentus_live_sk_");
  const isValidLooking = isTestKey || isLiveKey;

  const runRequest = async () => {
    if (!apiKey.trim()) {
      toast.error("Cole sua chave de API para executar.");
      return;
    }
    setLoading(true);
    setResponse(null);

    let bodyPayload = null;
    if (preset.body) {
      try {
        bodyPayload = JSON.parse(bodyText);
      } catch (e) {
        toast.error("JSON inválido no body.");
        setLoading(false);
        return;
      }
    }

    const start = performance.now();
    try {
      const backend =
        (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) ||
        (typeof window !== "undefined" ? window.location.origin : "");
      const url = `${backend}/api/v1${preset.path}`;
      const res = await fetch(url, {
        method: preset.method,
        headers: {
          "Authorization": `Bearer ${apiKey.trim()}`,
          "Content-Type": "application/json",
          ...(preset.method === "POST" ? { "Idempotency-Key": `playground-${Date.now()}` } : {}),
        },
        body: preset.method === "POST" && bodyPayload ? JSON.stringify(bodyPayload) : undefined,
      });
      const elapsedMs = Math.round(performance.now() - start);
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = text; }
      setResponse({
        status: res.status,
        ok: res.ok,
        elapsedMs,
        body: parsed,
      });
      if (res.ok) toast.success(`${res.status} em ${elapsedMs}ms`);
      else toast.error(`${res.status} · ${(parsed?.title) || "erro"}`);
    } catch (e) {
      setResponse({
        status: 0,
        ok: false,
        elapsedMs: Math.round(performance.now() - start),
        body: { error: String(e) },
      });
      toast.error("Falha na conexão.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      id="playground"
      eyebrow="Playground"
      title="Teste a API na hora."
      subtitle="Cole sua chave test, escolha uma operação e execute — a resposta real aparece do lado. Sem sair da doc."
    >
      <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
        {/* Coluna esquerda: setup + request */}
        <Card>
          {/* API key input */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-medium mb-2 flex items-center gap-2">
              <Key size={12} /> Sua chave de API
            </div>
            <div className="relative">
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => saveKey(e.target.value)}
                placeholder="agentus_test_sk_..."
                data-testid="playground-key-input"
                className="w-full h-11 bg-white/[0.03] border border-white/10 rounded-lg px-3 pr-10 text-[13px] font-mono text-emerald-200 placeholder-white/25 focus:outline-none focus:border-emerald-400/50 focus:bg-white/[0.06] transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-white transition-colors"
                aria-label={showKey ? "Ocultar" : "Mostrar"}
              >
                {showKey ? "•••" : "abc"}
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2 text-[11px]">
              {isTestKey && (
                <Pill tone="amber">TEST · sandbox seguro</Pill>
              )}
              {isLiveKey && (
                <Pill tone="green">LIVE · efeitos reais</Pill>
              )}
              {!isValidLooking && apiKey.trim().length > 0 && (
                <span className="text-red-300/80">
                  ⚠ Chave deve começar com <code className="font-mono">agentus_test_sk_</code> ou <code className="font-mono">agentus_live_sk_</code>
                </span>
              )}
              {!apiKey.trim() && (
                <span className="text-white/35">Gere no painel: Configuração › Desenvolvedor</span>
              )}
            </div>
          </div>

          {/* Preset picker */}
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-medium mb-2 flex items-center gap-2">
              <Sparkle size={12} /> Operação
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {PLAYGROUND_PRESETS.map((p) => {
                const active = preset.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => selectPreset(p)}
                    data-testid={`playground-preset-${p.id}`}
                    className={`px-3 py-2 rounded-lg border text-left transition-all ${
                      active
                        ? "border-emerald-400/40 bg-emerald-400/[0.06]"
                        : "border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <MethodBadge method={p.method} />
                    </div>
                    <div className="text-[12px] text-white/85 font-medium tracking-tight">{p.label}</div>
                    <code className="text-[10.5px] font-mono text-white/40">{p.path}</code>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Body editor (POST only) */}
          {preset.body && (
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-medium mb-2">
                Body (JSON)
              </div>
              <textarea
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                rows={8}
                data-testid="playground-body-editor"
                className="w-full font-mono text-[12px] bg-[#0A0D14] border border-white/[0.08] rounded-lg p-3 text-emerald-200/90 focus:outline-none focus:border-emerald-400/40 resize-none"
                spellCheck={false}
              />
            </div>
          )}

          <button
            onClick={runRequest}
            disabled={loading}
            data-testid="playground-execute"
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-white text-[#0B0F17] font-semibold text-[13px] hover:bg-slate-100 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <span className="w-3 h-3 rounded-full border-[1.5px] border-slate-900/25 border-t-slate-900 animate-spin" />
                Executando...
              </>
            ) : (
              <>
                <PlayCircle size={14} weight="fill" />
                Executar {preset.method} {preset.path}
              </>
            )}
          </button>

          <p className="text-[10.5px] text-white/30 mt-3 text-center leading-relaxed">
            Sua chave fica salva apenas no seu navegador (localStorage). Nunca é
            enviada pra nenhum servidor além do AgentUS.
          </p>
        </Card>

        {/* Coluna direita: response viewer */}
        <Card className="flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-medium mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Terminal size={12} /> Resposta
            </span>
            {response && (
              <div className="flex items-center gap-2">
                <StatusBadge status={response.status} ok={response.ok} />
                <span className="text-white/40 font-mono normal-case tracking-normal">
                  {response.elapsedMs}ms
                </span>
              </div>
            )}
          </div>
          {!response ? (
            <div className="flex-1 flex items-center justify-center py-16 text-center">
              <div>
                <PlayCircle size={32} className="text-white/15 mx-auto mb-3" />
                <p className="text-[13px] text-white/40 tracking-tight">
                  Cole sua chave, escolha uma operação e execute.<br />
                  A resposta real aparece aqui.
                </p>
              </div>
            </div>
          ) : (
            <pre
              className="flex-1 text-[12px] leading-[1.55] font-mono text-emerald-200/90 overflow-auto rounded-lg border border-white/[0.06] bg-[#0A0D14] p-3 whitespace-pre-wrap break-all min-h-[280px] max-h-[420px]"
              data-testid="playground-response"
            >
              {typeof response.body === "string"
                ? response.body
                : JSON.stringify(response.body, null, 2)}
            </pre>
          )}
        </Card>
      </div>
    </Section>
  );
}

function StatusBadge({ status, ok }) {
  const tone = status === 0
    ? "border-red-400/25 text-red-300 bg-red-400/[0.06]"
    : ok
      ? "border-emerald-400/25 text-emerald-300 bg-emerald-400/[0.06]"
      : status < 500
        ? "border-amber-400/25 text-amber-300 bg-amber-400/[0.06]"
        : "border-red-400/25 text-red-300 bg-red-400/[0.06]";
  return (
    <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded border ${tone}`}>
      {status === 0 ? "NETWORK" : status}
    </span>
  );
}

function Webhooks() {
  const events = [
    "customer.created", "customer.updated",
    "appointment.created", "appointment.cancelled",
    "product.created", "product.updated", "product.stock_changed",
    "pending_question.created",
  ];

  const validationCode = `import hmac, hashlib

def validate_agentus_signature(secret, header_signature, raw_body):
    """
    header_signature: valor do header X-Agentus-Signature
       (formato: "t=1708348331,v1=abc123...")
    raw_body: corpo do request EM BYTES, exatamente como recebido
    """
    parts = dict(p.split("=", 1) for p in header_signature.split(","))
    ts = parts.get("t")
    v1 = parts.get("v1")
    if not ts or not v1:
        return False
    expected = hmac.new(
        secret.encode("utf-8"),
        f"{ts}.{raw_body.decode('utf-8')}".encode("utf-8"),
        hashlib.sha256,
    ).hexdigest()
    return hmac.compare_digest(expected, v1)`;

  return (
    <Section
      id="webhooks"
      eyebrow="Bidirecional"
      title="Webhooks de saída."
      subtitle="O AgentUS notifica seu sistema quando algo importante acontece. Assinado com HMAC-SHA256, retry automático em janela de 32h."
    >
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/70 font-medium mb-3">
            Eventos disponíveis
          </div>
          <div className="flex flex-wrap gap-1.5">
            {events.map((e) => (
              <code
                key={e}
                className="text-[11.5px] font-mono px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-white/70"
              >
                {e}
              </code>
            ))}
          </div>

          <div className="mt-8 space-y-4">
            <RetryStep n="1ª" desc="Envio imediato" />
            <RetryStep n="2ª" desc="Após 1 minuto" />
            <RetryStep n="3ª" desc="Após 5 minutos" />
            <RetryStep n="4ª" desc="Após 30 minutos" />
            <RetryStep n="5ª" desc="Após 2 horas" />
            <RetryStep n="6ª" desc="Após 6 horas" />
            <RetryStep n="7ª" desc="Após 24 horas (última)" />
          </div>
          <p className="text-[11px] text-white/35 mt-6 leading-relaxed">
            Auto-pausa após 20 falhas consecutivas. Gestor reativa no painel após
            corrigir o receptor.
          </p>
        </Card>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/70 font-medium mb-3">
            Validação da assinatura HMAC (Python)
          </div>
          <CodeBlock code={validationCode} lang="Python" />
          <p className="text-[11.5px] text-white/40 mt-3 leading-relaxed">
            Compare em tempo constante. Rejeite qualquer request com assinatura
            inválida — assim ninguém consegue chamar seu endpoint fingindo ser
            o AgentUS.
          </p>
        </div>
      </div>
    </Section>
  );
}

function RetryStep({ n, desc }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10.5px] font-mono text-white/35 w-6">{n}</span>
      <span className="w-1 h-1 rounded-full bg-emerald-400/60" />
      <span className="text-[12.5px] text-white/60 tracking-tight">{desc}</span>
    </div>
  );
}

function Errors() {
  const errors = [
    { code: "401", title: "missing_credentials", fix: "Adicione header Authorization Bearer" },
    { code: "401", title: "invalid_api_key", fix: "Chave inválida, revogada ou malformada" },
    { code: "403", title: "insufficient_scope", fix: "Chave sem o escopo necessário" },
    { code: "403", title: "tenant_blocked", fix: "Assinatura do tenant está suspensa" },
    { code: "409", title: "duplicate_sku / duplicate_phone", fix: "Registro já existe com esse identificador" },
    { code: "422", title: "validation_error", fix: "Body inválido — veja errors[]" },
    { code: "422", title: "idempotency_key_reused", fix: "Mesma chave usada com body diferente" },
    { code: "429", title: "rate_limited", fix: "Aguarde e tente novamente" },
  ];
  return (
    <Section
      id="errors"
      eyebrow="Padrão RFC 7807"
      title="Erros previsíveis."
      subtitle="Todos os erros seguem o padrão Problem Details for HTTP APIs — type, title, status, detail, instance."
    >
      <Card>
        <div className="mb-6">
          <CodeBlock
            code={`{
  "type": "https://docs.agentus.com.br/errors/insufficient-scope",
  "title": "insufficient_scope",
  "status": 403,
  "detail": "API key missing required scope: 'products:write'.",
  "instance": "/api/v1/products",
  "required_scope": "products:write"
}`}
            lang="JSON de erro"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {errors.map((e, i) => (
            <div key={i} className="flex items-start gap-3 py-1.5">
              <span className={`shrink-0 text-[10px] font-mono font-semibold w-8 text-center py-0.5 rounded border ${
                e.code.startsWith("4") && e.code !== "429"
                  ? "border-red-400/25 text-red-300 bg-red-400/[0.05]"
                  : "border-amber-400/25 text-amber-300 bg-amber-400/[0.05]"
              }`}>{e.code}</span>
              <div className="flex-1 min-w-0">
                <code className="text-[11.5px] font-mono text-white/85">{e.title}</code>
                <p className="text-[11px] text-white/40 mt-0.5">{e.fix}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </Section>
  );
}

function FAQ() {
  const items = [
    {
      q: "O dashboard do AgentUS deixa de existir depois que integro?",
      a: "Não. O dashboard continua sendo usado — histórico do WhatsApp, perguntas pendentes, estatísticas de atendimento e configuração da IA vivem lá e não migram pro ERP. A integração só evita redigitação: dados que ambos os sistemas usam (cliente, produto, agendamento) ficam sincronizados por baixo, sem intervenção humana.",
    },
    {
      q: "Preciso de credencial de produção diferente da de teste?",
      a: "Sim. São duas chaves com prefixos diferentes: agentus_test_sk_ e agentus_live_sk_. A test não consome créditos nem envia mensagens de verdade, mas a API é 100% idêntica. Use test no dev/staging.",
    },
    {
      q: "Como rotaciono uma chave sem downtime?",
      a: "Gera nova chave no painel → configura a integração pra usar ela → revoga a antiga. As duas ficam válidas simultaneamente durante o handover.",
    },
    {
      q: "Meu endpoint recebe o webhook mas o AgentUS diz que falhou?",
      a: "Verifique se está retornando HTTP 2xx (200-299) em menos de 10 segundos. Qualquer outro código, ou timeout, conta como falha e vira retry. Responda 200 rapidamente e enfile o processamento numa fila interna.",
    },
    {
      q: "Existe SDK oficial em Python/Node/PHP?",
      a: "Ainda não. Mas a API é REST puro — funciona com requests (Python), axios (Node), Guzzle (PHP), qualquer cliente HTTP. O overhead de implementar direto é mínimo.",
    },
    {
      q: "Como faço para deletar produtos pelo bulk-upsert?",
      a: "Envie o produto no payload com \"active\": false. Isso é um soft-delete — mantém histórico mas para de considerar o produto ativo. Produtos existentes fora do payload NÃO são apagados.",
    },
    {
      q: "Consigo consultar quais webhooks já foram entregues?",
      a: "Sim. No painel: Configuração › Desenvolvedor › Webhooks › Ver últimas entregas. Cada tentativa tem status, código HTTP recebido e botão de refazer.",
    },
    {
      q: "A API tem versionamento? Vai quebrar depois?",
      a: "Sim, todos os endpoints são /api/v1/. Endpoints v1 são estáveis — não vão quebrar contrato. Uma futura v2 seria adicionada sem afetar v1.",
    },
  ];
  return (
    <Section
      id="faq"
      eyebrow="Perguntas frequentes"
      title="Dúvidas comuns."
    >
      <div className="space-y-2">
        {items.map((it, i) => <FaqItem key={i} q={it.q} a={it.a} />)}
      </div>
    </Section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border transition-colors ${
      open ? "border-white/[0.12] bg-white/[0.03]" : "border-white/[0.05]"
    }`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 py-4 flex items-start justify-between gap-4"
      >
        <span className="text-[13.5px] text-white font-medium tracking-tight">{q}</span>
        <CaretDown
          size={14}
          className={`text-white/40 shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1 text-[13px] text-white/55 leading-relaxed tracking-tight">
          {a}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────

export default function Developers() {
  return (
    <div
      className="relative min-h-screen text-white overflow-hidden"
      style={{ backgroundColor: "#04060B" }}
      data-testid="developers-page"
    >
      {/* Grid pontilhado sutil global */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
          backgroundSize: "34px 34px",
        }}
      />

      {/* Nav sticky */}
      <header
        className="sticky top-0 z-40 border-b border-white/[0.05]"
        style={{
          background: "rgba(4,6,11,0.72)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3.5">
          <a href="https://agentus.com.br" className="inline-flex items-center gap-2">
            <img
              src="/logo-white.png"
              alt="AgentUS"
              className="h-[15px] w-auto select-none opacity-95"
              draggable="false"
            />
            <span className="text-[10.5px] uppercase tracking-[0.2em] text-white/40 border-l border-white/15 pl-3 ml-1 hidden sm:inline">
              Developers
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-6 text-[12px] text-white/50">
            <a href="#quickstart" className="hover:text-white transition-colors">Começar</a>
            <a href="#endpoints" className="hover:text-white transition-colors">Endpoints</a>
            <a href="#playground" className="hover:text-white transition-colors">Playground</a>
            <a href="#webhooks" className="hover:text-white transition-colors">Webhooks</a>
            <a href="#errors" className="hover:text-white transition-colors">Erros</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </nav>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-[12px] text-white/70 hover:text-white transition-colors"
          >
            Acessar painel
            <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6">
        <Hero />
        <QuickStart />
        <Endpoints />
        <Playground />
        <Webhooks />
        <Errors />
        <FAQ />

        {/* CTA final */}
        <section className="my-16 md:my-24">
          <Card className="text-center py-14">
            <div className="text-[9.5px] uppercase tracking-[0.3em] text-emerald-300/70 font-medium mb-3">
              Próximo passo
            </div>
            <h2 className="font-heading text-[26px] md:text-[34px] font-semibold text-white tracking-[-0.028em] leading-tight">
              Pegue sua chave e comece agora.
            </h2>
            <p className="text-[13.5px] text-white/50 mt-3 max-w-lg mx-auto leading-relaxed">
              Ambiente de teste grátis. API idêntica em produção. Suporte técnico
              direto com o time.
            </p>
            <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-[#0B0F17] font-semibold text-[13px] hover:bg-slate-100 transition-colors"
              >
                Acessar painel
                <ArrowRight size={13} weight="bold" />
              </Link>
              <a
                href="mailto:contato@agentus.com.br"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white/80 font-medium text-[13px] hover:bg-white/[0.04] transition-colors"
              >
                Falar com o time
              </a>
            </div>
          </Card>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/[0.05] py-8 mt-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/30 tracking-[0.04em]">
          <span>© {new Date().getFullYear()} AgentUS · REST · JSON · Webhooks · RFC 7807 · Idempotent</span>
          <div className="flex items-center gap-5">
            <a href="https://agentus.com.br" className="hover:text-white/60">Site institucional</a>
            <a href="https://agentus.com.br/gov" className="hover:text-white/60">Gestão Pública</a>
          </div>
        </div>
      </footer>

      <Toaster position="top-right" richColors theme="dark" />
    </div>
  );
}
